import { useCallback, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";

type LiveCTA = {
  type: string;
  label: string;
  url: string;
  emoji: string;
};

type LiveGeo = {
  province: string | null;
  city: string | null;
  confidence: string;
};

type LivePrivacy = {
  isAnonymous: boolean;
  isHosting: boolean;
  isAbuser: boolean;
};

/**
 * Variante dual A/B/C resuelta por el worker para el visitor actual (sticky
 * bucket + cascada geo). Mismo shape que `DualVariantConfig` de
 * serverTemplateDetection, replicado acá para no acoplar el hook al tipo
 * server-side. Si el snapshot no incluye dualVariant (no hay cookie bucket
 * todavía, o no hay assignments en KV), el campo viene `null` y el consumidor
 * cae al seed de `__TEMPLATE_CONFIG__.dualVariant`.
 */
type LiveDualVariant = {
  id: string;
  displayName?: string;
  muxPlaybackId?: string;
  vipLabel?: string;
  vipSubtitle?: string;
  freeLabel?: string;
  priceUsd?: number;
  priceArs?: number;
  stripePriceId?: string;
  /** Promotion Code propio (promo_*) — descuento per-país aplicado a su price. */
  stripePromotionCodeId?: string;
  /** false = sin cupón (toggle admin). */
  stripePromoEnabled?: boolean;
  mpVariantLabel?: string;
  freeTelegramUrl?: string;
  freeEnabled?: boolean;
  /** Capa de presentación (theming + secciones) — blob opaco; consumido por DualCTAVideoLanding. */
  presentation?: Record<string, unknown>;
  assignmentScope?: string;
  assignmentGeo?: string;
};

/**
 * Dual CTA surface (intl `/` vs serMiembro `/ser-miembro` o `/membership`).
 * El worker la resuelve desde el pathname del request y la inyecta en
 * __TEMPLATE_CONFIG__.dualSurface + snapshot. El front NUNCA mapea pathname
 * → surface por su cuenta.
 */
export type DualSurface = "intl" | "serMiembro";

/**
 * Visibility per-surface pre-resuelta por el worker desde `dual_cta_visibility`
 * en KV. Si KV está vacío (admin nunca tocó la página o refresh pendiente), el
 * worker omite el campo y el consumer cae al hook `useDualCtaVisibility` que
 * pega directo a Railway (fallback documentado por el usuario).
 */
type LiveDualCtaVisibility = {
  vipEnabled: boolean;
  freeEnabled: boolean;
  vipDisabled: boolean;
  freeDisabled: boolean;
  /** Label override pre-mergeado para la surface activa, lang-correct. null = sin override. */
  vipLabel: string | null;
  freeLabel: string | null;
};

/**
 * Labels admin DB (stripe_checkout_config.dual_cta_*) pre-resueltos por el
 * worker al binary lang del visitor. Equivalente a lo que devuelve el hook
 * `useDualCtaLabels` — la diferencia es que vienen vía worker (KV-driven,
 * live al cambio del admin) en lugar de Railway direct fetch-on-mount.
 * null = KV vacío → consumer cae al hook fallback.
 */
type LiveDualCtaLabels = {
  paid: string | null;
  paidSubtitle: string | null;
  free: string | null;
};

export type LiveCTAState = {
  cta: LiveCTA;
  geo: LiveGeo;
  /**
   * Privacy flags expuestos solo cuando la detección vino del path iplocate
   * (Capa 2 — iplocate.io). Para el resto de paths el campo es `null` — el
   * caller NO debe asumir que privacy === null significa "humano confiable",
   * solo significa "sin señal".
   */
  privacy: LivePrivacy | null;
  /**
   * Dual CTA variant resuelta en vivo. `null` hasta que el primer poll del
   * snapshot la traiga (o si el visitor no tiene bucket / KV vacío). El
   * consumidor (`DualCTAVideoLanding`) debe seguir leyendo el seed inicial de
   * `__TEMPLATE_CONFIG__.dualVariant` y solo pisarlo cuando dualVariant !== null.
   */
  dualVariant: LiveDualVariant | null;
  /** Surface decidida por el worker — `null` solo si el SSR no la inyectó. */
  dualSurface: DualSurface | null;
  /** Visibility + label overrides pre-resueltos por el worker. null = sin KV → fallback Railway. */
  dualCtaVisibility: LiveDualCtaVisibility | null;
  /** Labels admin DB (paid/free/subtitle) pre-resueltos por el worker. null = sin KV → fallback hook. */
  dualCtaLabels: LiveDualCtaLabels | null;
};

type TemplateConfigShape = {
  ctaType?: unknown;
  ctaLabel?: unknown;
  ctaUrl?: unknown;
  ctaEmoji?: unknown;
  province?: unknown;
  city?: unknown;
  geoConfidence?: unknown;
  ctaVersion?: unknown;
  cachedAt?: unknown;
  /** Idioma resuelto por el worker en el SSR (es/en/…) — paridad con el snapshot fetch. */
  language?: string;
  // Worker emite __TEMPLATE_CONFIG__.dualVariant en el SSR cuando la cookie
  // bucket existe Y hay assignment en KV. Esto permite que VideoTemplate
  // tenga la variante asignada al PRIMER paint, sin esperar el poll del
  // snapshot (~100-300ms). Sin seed, el visitor vería labels default por
  // unas centésimas hasta que arranque el polling.
  dualVariant?: unknown;
  /** Surface (intl|serMiembro) que el worker decidió a partir del pathname del SSR. */
  dualSurface?: unknown;
  /** Visibility + label overrides pre-mergeados por el worker. */
  dualCtaVisibility?: unknown;
  /** Labels admin DB (paid/free/subtitle) pre-mergeados. */
  dualCtaLabels?: unknown;
};

type SnapshotResponse = {
  version?: unknown;
  dualVersion?: unknown;
  cta?: {
    type?: unknown;
    label?: unknown;
    url?: unknown;
    emoji?: unknown;
  };
  geo?: {
    province?: unknown;
    city?: unknown;
    confidence?: unknown;
  };
  privacy?: {
    isAnonymous?: unknown;
    isHosting?: unknown;
    isAbuser?: unknown;
  };
  dualVariant?: {
    id?: unknown;
    muxPlaybackId?: unknown;
    vipLabel?: unknown;
    vipSubtitle?: unknown;
    freeLabel?: unknown;
    priceUsd?: unknown;
    priceArs?: unknown;
    stripePriceId?: unknown;
    stripePromotionCodeId?: unknown;
    mpVariantLabel?: unknown;
    freeTelegramUrl?: unknown;
    freeEnabled?: unknown;
    assignmentScope?: unknown;
    assignmentGeo?: unknown;
  };
  dualSurface?: unknown;
  dualCtaVisibility?: {
    vipEnabled?: unknown;
    freeEnabled?: unknown;
    vipDisabled?: unknown;
    freeDisabled?: unknown;
    vipLabel?: unknown;
    freeLabel?: unknown;
  };
  dualCtaLabels?: {
    paid?: unknown;
    paidSubtitle?: unknown;
    free?: unknown;
  };
};

// Poll del snapshot endpoint. Bajamos de 10s a 3s (2026-06) para que
// SPA visitors vean cambios admin en ≤3s en vez de ≤10s. El snapshot endpoint
// es ~50ms KV-hit, así que el costo por visitor es ~1KB/3s = ~20KB/min — bajo
// para cualquier presupuesto razonable. Background tabs siguen throttle del
// browser (≥1s), así que no abusa de tabs minimizadas.
const POLL_INTERVAL_MS = 3_000;
const SNAPSHOT_ENDPOINT = "/api/cta-config-snapshot";

/** Cross-tab / same-tab signal tras save en /admin/dual-cta-variants. */
export const DUAL_CTA_VARIANTS_UPDATED_LS_KEY = "dual_cta_variants_updated";
export const DUAL_CTA_VARIANTS_UPDATED_EVENT = "dual-cta-variants-updated";

/**
 * Si la URL actual tiene `?_force_variant=X` (admin preview), lo propaga al
 * snapshot endpoint. Sin esto, el worker en hard nav forza la variante en el
 * seed inicial (__TEMPLATE_CONFIG__.dualVariant) pero el polling al snapshot
 * cada 10s la pisa con la variante bucket-based real, rompiendo el preview
 * después del primer poll.
 */
function buildSnapshotUrl(): string {
  if (typeof window === "undefined") return SNAPSHOT_ENDPOINT;
  const params = new URLSearchParams();
  // visitorSearch a scope de función: lo usan los tres bloques try (force/debug
  // y el de lang). Declararlo dentro del primer try lo dejaba fuera de scope en
  // el bloque de `lang` → ReferenceError tragado por el catch → ?lang nunca se
  // propagaba al snapshot (fidelidad ES/EN del preview admin rota).
  let visitorSearch: URLSearchParams;
  try {
    visitorSearch = new URLSearchParams(window.location.search);
  } catch {
    visitorSearch = new URLSearchParams();
  }
  try {
    // _force_variant: admin preview de una variante específica.
    const forced = visitorSearch.get("_force_variant");
    if (forced && forced.trim()) {
      params.set("_force_variant", forced.trim());
    }
    // ── Admin debug preview (?debug=SECRET&country=US&state=FL&province=…&city=…)
    // Propagamos los 4 params al snapshot fetch para que el polling cada 3s
    // siga emitiendo datos del país previsualizado en lugar de pisar con el
    // geo real del admin. Sin esto, el preview "as US visitor" funcionaba en
    // el primer paint pero el snapshot lo rompía a los 3s.
    // El worker gatea estos params con DEBUG_SECRET (legacy) o con la cookie
    // geo_sim_token (super admin, marker ?geo_sim=1) — si la autorización no es
    // válida, los ignora silenciosamente (no es un leak).
    const debugKey = visitorSearch.get("debug");
    const geoSim = visitorSearch.get("geo_sim") === "1";
    if ((debugKey && debugKey.trim()) || geoSim) {
      if (debugKey && debugKey.trim()) params.set("debug", debugKey.trim());
      if (geoSim) {
        params.set("geo_sim", "1");
        // gst: token de simulación (camino robusto, no depende de la cookie).
        const gst = visitorSearch.get("gst");
        if (gst && gst.trim()) params.set("gst", gst.trim());
      }
      const previewFields = ["country", "state", "province", "city"] as const;
      for (const field of previewFields) {
        const value = visitorSearch.get(field);
        if (value && value.trim()) {
          params.set(field, value.trim());
        }
      }
      // Forward VPN simulation so that admin previews consistently apply real-VPN
      // gates (hideCta, privacy flags, BaseTemplate downgrade) in both initial render
      // and live snapshot polls.
      // This is critical for the Clerk-validated super-admin geo sim path
      // ("if im admin validate from clerk").
      const simulateVpn = visitorSearch.get("simulate_vpn") || visitorSearch.get("vpn_sim");
      if (simulateVpn && simulateVpn.trim()) {
        params.set("simulate_vpn", simulateVpn.trim());
      }
      const simulateIp = visitorSearch.get("simulate_ip");
      if (simulateIp && simulateIp.trim()) {
        params.set("simulate_ip", simulateIp.trim());
      }
    }
  } catch {
    // best-effort
  }
  // path → el worker la usa para resolver `dualSurface` (intl|serMiembro) y
  // pre-mergear la visibility correcta. Sin esto el worker asumiría intl por
  // default y los visitors de /ser-miembro recibirían flags de la surface
  // equivocada. NO es un setting, es metadata estructural del pageview.
  try {
    params.set("path", window.location.pathname || "/");
  } catch {
    // best-effort — sin path el worker default a intl
  }
  // lang → paridad SSR: el snapshot usa la misma política que __TEMPLATE_CONFIG__.language.
  // ?lang= en la URL (preview admin) pisa localStorage para que ES/EN del editor sea fiel.
  try {
    const cfg = (window as unknown as { __TEMPLATE_CONFIG__?: TemplateConfigShape })
      .__TEMPLATE_CONFIG__;
    const urlLang = visitorSearch.get("lang");
    const userLang =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("user_language") || localStorage.getItem("i18nextLng")
        : null;
    const lang = (urlLang?.trim() || userLang || cfg?.language || "").trim();
    if (lang) params.set("lang", lang);
  } catch {
    // best-effort
  }
  const qs = params.toString();
  return qs ? `${SNAPSHOT_ENDPOINT}?${qs}` : SNAPSHOT_ENDPOINT;
}

function toStr(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function toNullableStr(value: unknown): string | null {
  if (typeof value === "string" && value.length > 0) return value;
  return null;
}

function toVersion(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function toNumOrUndef(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function toStrOrUndef(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value;
  return undefined;
}

/**
 * Parsea un dualVariant raw (de cualquier fuente: snapshot endpoint o
 * __TEMPLATE_CONFIG__ inyectado por el worker). Defensive — acepta `unknown`
 * y retorna null si el shape no es válido (id obligatorio). Sin esto, no
 * podemos seedear desde __TEMPLATE_CONFIG__ porque su tipo es estricto
 * (`DualVariantConfig`) y SnapshotResponse usa unknowns — preferimos shape
 * único para evitar mantener dos parsers en paralelo.
 */
function parseDualSurface(raw: unknown): DualSurface | null {
  return raw === "intl" || raw === "serMiembro" ? raw : null;
}

function parseDualCtaVisibility(raw: unknown): LiveDualCtaVisibility | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  return {
    // Defaults conservadores: cualquier booleano ausente → tratamos como
    // "enabled" porque el worker omite el campo cuando el KV no fue tocado.
    // Si admin desactivó un botón, vendrá `false` explícito.
    vipEnabled:   obj.vipEnabled  !== false,
    freeEnabled:  obj.freeEnabled !== false,
    vipDisabled:  obj.vipDisabled === true,
    freeDisabled: obj.freeDisabled === true,
    vipLabel:    typeof obj.vipLabel  === "string" && obj.vipLabel.length  > 0 ? obj.vipLabel  : null,
    freeLabel:   typeof obj.freeLabel === "string" && obj.freeLabel.length > 0 ? obj.freeLabel : null,
  };
}

function parseDualCtaLabels(raw: unknown): LiveDualCtaLabels | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  return {
    paid:         typeof obj.paid         === "string" && obj.paid.length         > 0 ? obj.paid         : null,
    paidSubtitle: typeof obj.paidSubtitle === "string" && obj.paidSubtitle.length > 0 ? obj.paidSubtitle : null,
    free:         typeof obj.free         === "string" && obj.free.length         > 0 ? obj.free         : null,
  };
}

function liveDualVariantsDiffer(
  prev: LiveDualVariant | null,
  next: LiveDualVariant | null,
): boolean {
  if (!next) return false;
  if (!prev) return true;
  return (
    prev.id !== next.id ||
    prev.muxPlaybackId !== next.muxPlaybackId ||
    prev.vipLabel !== next.vipLabel ||
    prev.vipSubtitle !== next.vipSubtitle ||
    prev.freeLabel !== next.freeLabel ||
    prev.priceUsd !== next.priceUsd ||
    prev.priceArs !== next.priceArs ||
    prev.stripePriceId !== next.stripePriceId ||
    prev.stripePromotionCodeId !== next.stripePromotionCodeId ||
    prev.stripePromoEnabled !== next.stripePromoEnabled ||
    prev.mpVariantLabel !== next.mpVariantLabel ||
    prev.freeTelegramUrl !== next.freeTelegramUrl ||
    prev.freeEnabled !== next.freeEnabled
  );
}

/** Content-diff cuando cta_config_version no bumpea (gap CF API / refresh). */
function liveCtaContentDiffers(
  prev: LiveCTAState["cta"] | null,
  next: LiveCTAState["cta"],
): boolean {
  if (!prev) return !!(next.type || next.label || next.url || next.emoji);
  return (
    prev.type !== next.type ||
    prev.label !== next.label ||
    prev.url !== next.url ||
    prev.emoji !== next.emoji
  );
}

/**
 * Labels/visibility viven en KV sin *_version. Sin este diff, useLiveCTA
 * early-returna cuando solo cambian labels/kill-switches (hueco CF/KV).
 */
function liveVisibilityDiffers(
  prev: LiveDualCtaVisibility | null,
  next: LiveDualCtaVisibility | null,
): boolean {
  if (!next) return false;
  if (!prev) return true;
  return (
    prev.vipEnabled !== next.vipEnabled ||
    prev.freeEnabled !== next.freeEnabled ||
    prev.vipDisabled !== next.vipDisabled ||
    prev.freeDisabled !== next.freeDisabled ||
    prev.vipLabel !== next.vipLabel ||
    prev.freeLabel !== next.freeLabel
  );
}

function liveLabelsDiffers(
  prev: LiveDualCtaLabels | null,
  next: LiveDualCtaLabels | null,
): boolean {
  if (!next) return false;
  if (!prev) return true;
  return (
    prev.paid !== next.paid ||
    prev.paidSubtitle !== next.paidSubtitle ||
    prev.free !== next.free
  );
}

function parseDualVariant(raw: unknown): LiveDualVariant | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const id = toStrOrUndef(obj.id);
  if (!id) return null;
  return {
    id,
    displayName: toStrOrUndef(obj.displayName),
    muxPlaybackId: toStrOrUndef(obj.muxPlaybackId),
    vipLabel: toStrOrUndef(obj.vipLabel),
    vipSubtitle: toStrOrUndef(obj.vipSubtitle),
    freeLabel: toStrOrUndef(obj.freeLabel),
    priceUsd: toNumOrUndef(obj.priceUsd),
    priceArs: toNumOrUndef(obj.priceArs),
    stripePriceId: toStrOrUndef(obj.stripePriceId),
    stripePromotionCodeId: toStrOrUndef(obj.stripePromotionCodeId),
    stripePromoEnabled:
      typeof obj.stripePromoEnabled === "boolean"
        ? obj.stripePromoEnabled
        : typeof obj.stripe_promo_enabled === "boolean"
          ? obj.stripe_promo_enabled
          : undefined,
    mpVariantLabel: toStrOrUndef(obj.mpVariantLabel),
    freeTelegramUrl: toStrOrUndef(obj.freeTelegramUrl),
    freeEnabled: typeof obj.freeEnabled === "boolean" ? obj.freeEnabled : undefined,
    presentation:
      obj.presentation && typeof obj.presentation === "object" && !Array.isArray(obj.presentation)
        ? (obj.presentation as Record<string, unknown>)
        : undefined,
    assignmentScope: toStrOrUndef(obj.assignmentScope),
    assignmentGeo: toStrOrUndef(obj.assignmentGeo),
  };
}

function readInitialState(): {
  state: LiveCTAState;
  version: number;
  dualVersion: number;
} {
  if (typeof window === "undefined") {
    return {
      state: {
        cta: { type: "", label: "", url: "", emoji: "" },
        geo: { province: null, city: null, confidence: "" },
        privacy: null,
        dualVariant: null,
        dualSurface: null,
        dualCtaVisibility: null,
        dualCtaLabels: null,
      },
      version: 0,
      dualVersion: 0,
    };
  }
  const cfg = (window as unknown as { __TEMPLATE_CONFIG__?: TemplateConfigShape })
    .__TEMPLATE_CONFIG__;
  const raw: TemplateConfigShape = cfg ?? {};
  const version =
    raw.ctaVersion !== undefined && raw.ctaVersion !== null
      ? toVersion(raw.ctaVersion)
      : raw.cachedAt !== undefined && raw.cachedAt !== null
        ? toVersion(raw.cachedAt)
        : 0;
  return {
    state: {
      cta: {
        type: toStr(raw.ctaType),
        label: toStr(raw.ctaLabel),
        url: toStr(raw.ctaUrl),
        emoji: toStr(raw.ctaEmoji),
      },
      geo: {
        province: toNullableStr(raw.province),
        city: toNullableStr(raw.city),
        confidence: toStr(raw.geoConfidence),
      },
      // Privacy nunca viene en __TEMPLATE_CONFIG__ (es runtime-only del
      // snapshot endpoint). El primer poll lo poblará si la detección
      // pasó por iplocate.
      privacy: null,
      // Seed del dualVariant inyectado por el worker en hard nav. Evita el
      // flash visual en VideoTemplate (labels default por 100-300ms hasta
      // que el primer poll del snapshot traiga la variante). DualCTAVideoLanding
      // tambien lo lee independiente para su flow, pero acá lo exponemos al
      // hook para que CUALQUIER consumidor de useLiveCTA tenga la variante
      // desde el primer render. Si el worker no inyectó (sin cookie bucket
      // todavía, KV vacío), parseDualVariant retorna null y el primer poll
      // lo traerá si corresponde.
      dualVariant: parseDualVariant(raw.dualVariant),
      dualSurface: parseDualSurface(raw.dualSurface),
      dualCtaVisibility: parseDualCtaVisibility(raw.dualCtaVisibility),
      dualCtaLabels: parseDualCtaLabels(raw.dualCtaLabels),
    },
    version,
    // Sin seed para dualVersion — empieza en 0 para que el primer poll
    // siempre populate (cualquier version del worker > 0 lo va a actualizar).
    dualVersion: 0,
  };
}

// Cuántos fallos consecutivos toleramos antes de emitir un evento PostHog.
// Sin esto los visitors con red flaky generan ruido; con esto solo emitimos
// cuando hay un problema sistemático (ej. snapshot endpoint down, worker no
// responde, DNS). El evento se emite UNA vez por sesión hasta que volvemos a
// tener éxito (entonces el contador se reinicia).
const POLL_FAIL_TELEMETRY_THRESHOLD = 5;

/**
 * Poll del snapshot (/api/cta-config-snapshot). BaseTemplate lo usa para hero
 * CTA + geo en vivo. Superficies membresía/US comparten un solo poll vía
 * DualCtaLiveProvider → useDualCtaLive().
 */
export function useLiveCTAPoll(): LiveCTAState {
  const initial = useRef<{
    state: LiveCTAState;
    version: number;
    dualVersion: number;
  } | null>(null);
  if (initial.current === null) initial.current = readInitialState();

  const [state, setState] = useState<LiveCTAState>(initial.current.state);
  // Dos trackers de version independientes: el CTA regular y el dual variant
  // pueden bumpear por separado (admin puede tocar una sin la otra). Así
  // evitamos re-render del CTA cuando solo cambió dual, y viceversa.
  const latestVersion = useRef<number>(initial.current.version);
  const latestDualVersion = useRef<number>(initial.current.dualVersion);
  const latestDualVariantContent = useRef<LiveDualVariant | null>(
    initial.current.state.dualVariant,
  );
  const latestCtaContent = useRef<LiveCTAState["cta"]>(initial.current.state.cta);
  const latestVisibilityContent = useRef<LiveDualCtaVisibility | null>(
    initial.current.state.dualCtaVisibility,
  );
  const latestLabelsContent = useRef<LiveDualCtaLabels | null>(
    initial.current.state.dualCtaLabels,
  );
  const latestSurfaceContent = useRef<DualSurface | null>(
    initial.current.state.dualSurface,
  );
  // Contador de fallos consecutivos del polling. Resetea al primer éxito.
  // Emite UN evento PostHog cuando cruza el threshold para no spamear.
  const consecutiveFailsRef = useRef<number>(0);
  const telemetryFiredRef = useRef<boolean>(false);

  const fetchSnapshot = useCallback(async (options?: { bustCache?: boolean }): Promise<void> => {
    try {
      let url = buildSnapshotUrl();
      if (options?.bustCache) {
        url += `${url.includes("?") ? "&" : "?"}_t=${Date.now()}`;
      }
      const res = await fetch(url, {
        headers: { "Cache-Control": "no-cache" },
      });
      if (!res.ok) {
        // Contamos como fallo y, si cruzamos el threshold, emitimos UN evento
        // PostHog. Esto hace observable los problemas reales (snapshot endpoint
        // down, worker no responde) sin spamear por blips de red.
        consecutiveFailsRef.current += 1;
        if (
          consecutiveFailsRef.current >= POLL_FAIL_TELEMETRY_THRESHOLD &&
          !telemetryFiredRef.current
        ) {
          telemetryFiredRef.current = true;
          try {
            posthog.capture("hero_cta_snapshot_polling_failed", {
              consecutive_fails: consecutiveFailsRef.current,
              http_status: res.status,
              endpoint: SNAPSHOT_ENDPOINT,
            });
          } catch {
            /* posthog no inicializado */
          }
        }
        return;
      }
      // Éxito: reset del contador. Si habíamos emitido antes, la próxima
      // racha de fails sí puede emitir de nuevo (re-armamos telemetryFiredRef).
      consecutiveFailsRef.current = 0;
      telemetryFiredRef.current = false;
      const data = (await res.json()) as SnapshotResponse;
      const incomingVersion = toVersion(data.version);
      const incomingDualVersion = toVersion(data.dualVersion);
      const nextDualFromSnapshot = parseDualVariant(data.dualVariant);
      const nextCta = {
        type: toStr(data.cta?.type),
        label: toStr(data.cta?.label),
        url: toStr(data.cta?.url),
        emoji: toStr(data.cta?.emoji),
      };
      const nextSurface = parseDualSurface(data.dualSurface);
      const nextVisibility = parseDualCtaVisibility(data.dualCtaVisibility);
      const nextLabels = parseDualCtaLabels(data.dualCtaLabels);
      const ctaVersionBumped = incomingVersion > latestVersion.current;
      const ctaContentChanged = liveCtaContentDiffers(latestCtaContent.current, nextCta);
      const ctaChanged = ctaVersionBumped || ctaContentChanged;
      const dualVersionBumped = incomingDualVersion > latestDualVersion.current;
      const dualContentChanged = liveDualVariantsDiffer(
        latestDualVariantContent.current,
        nextDualFromSnapshot,
      );
      const dualChanged = dualVersionBumped || dualContentChanged;
      const metaChanged =
        liveVisibilityDiffers(latestVisibilityContent.current, nextVisibility) ||
        liveLabelsDiffers(latestLabelsContent.current, nextLabels) ||
        (!!nextSurface && nextSurface !== latestSurfaceContent.current);
      if (!ctaChanged && !dualChanged && !metaChanged) return;
      // Tracking: visitor recibió un cambio del admin. Capturamos antes de
      // mutar la version refs para preservar el delta exacto. version es
      // epoch-ms de cuándo el admin escribió → Date.now() - version = lag
      // total (DB write + KV propagation + poll wait). Útil para medir si
      // los ~10s típicos se cumplen en producción.
      const now = Date.now();
      const prevCtaType =
        typeof window !== "undefined" && window.posthog
          ? toStr((window as unknown as { __TEMPLATE_CONFIG__?: { ctaType?: unknown } })
              .__TEMPLATE_CONFIG__?.ctaType)
          : "";
      if (ctaChanged) {
        try {
          posthog.capture("hero_cta_snapshot_changed", {
            prev_version: latestVersion.current || undefined,
            new_version: incomingVersion,
            version_delta_ms: incomingVersion ? now - incomingVersion : undefined,
            prev_cta_type: prevCtaType || undefined,
            new_cta_type: nextCta.type || undefined,
            new_cta_label: nextCta.label || undefined,
            geo_province: toNullableStr(data.geo?.province) || undefined,
            geo_city: toNullableStr(data.geo?.city) || undefined,
          });
        } catch {
          // posthog no inicializado / fail silencioso — no romper polling
        }
        if (ctaVersionBumped) latestVersion.current = incomingVersion;
        latestCtaContent.current = nextCta;
      }
      if (dualVersionBumped) latestDualVersion.current = incomingDualVersion;
      // Privacy es opcional en el response (solo viene cuando AR + iplocate).
      // Si está ausente preservamos null — NO inferimos "humano" de la ausencia.
      const privacy: LivePrivacy | null = data.privacy
        ? {
            isAnonymous: !!data.privacy.isAnonymous,
            isHosting: !!data.privacy.isHosting,
            isAbuser: !!data.privacy.isAbuser,
          }
        : null;
      // setState con functional form: mantener dualVariant viejo cuando
      // dualVersion no avanzó. Sin esto un poll que solo bumpea CTA pisaría
      // el dual a null (snapshot omite dualVariant si no hay cookie bucket).
      // Functional form evita capturar state en el callback → fetchSnapshot
      // queda estable (sin deps) → no reinicia el polling interval.
      // dualSurface y dualCtaVisibility se traen siempre del snapshot — son
      // metadata por-request del worker (no versionada como cta/dual). Si el
      // worker omitió dualCtaVisibility (KV vacío) preservamos el valor previo
      // del SSR; el front cae a Railway directo cuando ambos quedan null.
      setState((prev) => {
        const nextDual =
          dualChanged && nextDualFromSnapshot ? nextDualFromSnapshot : prev.dualVariant;
        latestDualVariantContent.current = nextDual;
        const resolvedSurface = nextSurface ?? prev.dualSurface;
        const resolvedVisibility = nextVisibility ?? prev.dualCtaVisibility;
        const resolvedLabels = nextLabels ?? prev.dualCtaLabels;
        latestSurfaceContent.current = resolvedSurface;
        latestVisibilityContent.current = resolvedVisibility;
        latestLabelsContent.current = resolvedLabels;
        return {
          cta: nextCta,
          geo: {
            province: toNullableStr(data.geo?.province),
            city: toNullableStr(data.geo?.city),
            confidence: toStr(data.geo?.confidence),
          },
          privacy,
          dualVariant: nextDual,
          dualSurface: resolvedSurface,
          dualCtaVisibility: resolvedVisibility,
          dualCtaLabels: resolvedLabels,
        };
      });
    } catch {
      // Excepción (network error, JSON parse error). Mismo tratamiento que
      // !res.ok: contamos como fail y emitimos telemetry si cruza el threshold.
      consecutiveFailsRef.current += 1;
      if (
        consecutiveFailsRef.current >= POLL_FAIL_TELEMETRY_THRESHOLD &&
        !telemetryFiredRef.current
      ) {
        telemetryFiredRef.current = true;
        try {
          posthog.capture("hero_cta_snapshot_polling_failed", {
            consecutive_fails: consecutiveFailsRef.current,
            error_kind: "exception",
            endpoint: SNAPSHOT_ENDPOINT,
          });
        } catch {
          /* */
        }
      }
    }
  }, []);

  // Tras save en /admin/dual-cta-variants: fetch inmediato (+ retry) en vez de
  // esperar al poll de 3s. Cross-tab vía localStorage + mismo-tab vía event.
  const handleDualVariantsUpdated = useCallback((): void => {
    const pulse = (): void => {
      latestDualVersion.current = 0;
      latestDualVariantContent.current = null;
      void fetchSnapshot({ bustCache: true });
    };
    pulse();
    for (const delayMs of [350, 800, 1600]) {
      window.setTimeout(pulse, delayMs);
    }
  }, [fetchSnapshot]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = (): void => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void fetchSnapshot();
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = (): void => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void fetchSnapshot();
        startPolling();
      } else {
        stopPolling();
      }
    };

    const handleStorage = (event: StorageEvent): void => {
      if (event.key === DUAL_CTA_VARIANTS_UPDATED_LS_KEY) {
        handleDualVariantsUpdated();
      }
    };

    if (document.visibilityState === "visible") {
      // Mount inicial con tab visible: fetch inmediato para cerrar la ventana
      // entre el SSR del worker (cache de KV del isolate) y el primer poll.
      void fetchSnapshot();
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(DUAL_CTA_VARIANTS_UPDATED_EVENT, handleDualVariantsUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(DUAL_CTA_VARIANTS_UPDATED_EVENT, handleDualVariantsUpdated);
      window.removeEventListener("storage", handleStorage);
      stopPolling();
    };
  }, [fetchSnapshot, handleDualVariantsUpdated]);

  return state;
}

/** Hero CTA en BaseTemplate (poll propio por página). */
export function useLiveCTA(): LiveCTAState {
  return useLiveCTAPoll();
}
